import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { db, auth, storage } from "@/lib/firebase";
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, onSnapshot, orderBy, Timestamp, setDoc, limit, writeBatch } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject, getStorage } from "firebase/storage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, FileText, Download, MoreVertical, Tag, Trash2, Sparkles, Eye, Archive } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { UploadDocument } from "./UploadDocument";
import { ImportFromVaultModal } from "./ImportFromVaultModal";
import { format } from "date-fns";
import { dualReadJobDocuments, dualWriteJobDocument } from "@/lib/firestore-helpers";
import type { DocumentExtractionResult, ExtractedField } from "@/hooks/useCompliance";

// Compliance document categories - must match database attachment_categories codes
// These codes are used by the requirements checklist from Supabase
export const COMPLIANCE_CATEGORIES = [
  { value: "", label: "None (General Document)" },
  // Matches database attachment_categories codes exactly
  { value: "application_form", label: "Permit Application Form" },
  { value: "contractor_license", label: "Contractor License" },
  { value: "insurance_coi", label: "Certificate of Insurance" },
  { value: "load_calculation", label: "Load Calculation (Manual J/D/S)" },
  { value: "equipment_specs", label: "Equipment Specifications" },
  { value: "site_plan", label: "Site Plan" },
  { value: "riser_diagram", label: "Riser Diagram" },
  { value: "single_line", label: "Single Line Diagram" },
  { value: "energy_calc", label: "Energy Calculation" },
  { value: "product_approval", label: "Product Approval (NOA/FBC)" },
  { value: "sealed_drawings", label: "Sealed Drawings (Engineer/Architect)" },
  { value: "owner_auth", label: "Owner Authorization" },
  { value: "building_plans", label: "Building Plans" },
  { value: "survey", label: "Property Survey" },
];

type Document = {
  id: string;
  document_name: string;
  document_url: string;
  document_type: string;
  description: string;
  category: string | null;
  status: string | null;
  createdAt: string;
  extractedFields?: ExtractedField[];
  extractionConfidence?: number;
  extractionMethod?: string;
  extractedAt?: string;
};

// Human-readable field labels for extraction results
const FIELD_LABELS: Record<string, string> = {
  licenseNumber: "License Number",
  licenseName: "License Name",
  licenseType: "License Type",
  expirationDate: "Expiration Date",
  issueDate: "Issue Date",
  status: "Status",
  issuingAuthority: "Issuing Authority",
  policyNumber: "Policy Number",
  insuredName: "Insured Name",
  insurerName: "Insurer",
  effectiveDate: "Effective Date",
  generalLiabilityLimit: "General Liability",
  workersCompLimit: "Workers Comp",
  autoLiabilityLimit: "Auto Liability",
  umbrellaLimit: "Umbrella Coverage",
  totalCoolingLoad: "Cooling Load",
  totalHeatingLoad: "Heating Load",
  designTemperatures: "Design Temps",
  squareFootage: "Square Footage",
  propertyAddress: "Property Address",
  preparedBy: "Prepared By",
  preparedDate: "Prepared Date",
  softwareUsed: "Software Used",
  engineerStamp: "Engineer Stamp",
  noaNumber: "NOA Number",
  productName: "Product Name",
  manufacturer: "Manufacturer",
  approvalDate: "Approval Date",
  approvedUse: "Approved Use",
  windZone: "Wind Zone",
  impactRated: "Impact Rated",
  permitNumber: "Permit Number",
  propertyOwner: "Property Owner",
  contractorName: "Contractor",
  contractorLicense: "Contractor License",
  workDescription: "Work Description",
  estimatedValue: "Estimated Value",
  applicationDate: "Application Date",
};

type JobDocumentsProps = {
  jobId: string;
  contractorId: string;
  jobAddress?: string;
  initialCategory?: string;
  requirementName?: string;
  onDocumentsChange?: (documents: Document[]) => void;
};

export interface JobDocumentsHandle {
  openUpload: (category?: string, requirement?: string) => void;
  refresh: () => void;
}

export const JobDocuments = forwardRef<JobDocumentsHandle, JobDocumentsProps>(({
  jobId,
  contractorId,
  jobAddress = "",
  initialCategory,
  requirementName,
  onDocumentsChange
}, ref) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(!!initialCategory);
  const [showImportVault, setShowImportVault] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(initialCategory || "");
  const [uploadRequirement, setUploadRequirement] = useState(requirementName || "");
  const [editingCategoryDoc, setEditingCategoryDoc] = useState<Document | null>(null);
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);
  const { toast } = useToast();

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openUpload: (category?: string, requirement?: string) => {
      setUploadCategory(category || "");
      setUploadRequirement(requirement || "");
      setShowUpload(true);
    },
    refresh: () => {
      fetchDocuments();
    }
  }));

  useEffect(() => {
    fetchDocuments();
  }, [jobId, contractorId]);

  const fetchDocuments = async () => {
    try {
      const snapshot = await dualReadJobDocuments(contractorId, jobId, [
        orderBy("createdAt", "desc")
      ]);

      const data = snapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          document_name: docData.document_name || '',
          document_url: docData.document_url || '',
          document_type: docData.document_type || '',
          description: docData.description || '',
          category: docData.category || docData.complianceCategory || null,
          status: docData.status || null,
          createdAt: docData.createdAt?.toDate ? docData.createdAt.toDate().toISOString() : new Date().toISOString(),
          extractedFields: docData.extractedFields || undefined,
          extractionConfidence: docData.extractionConfidence || undefined,
          extractionMethod: docData.extractionMethod || undefined,
          extractedAt: docData.extractedAt?.toDate ? docData.extractedAt.toDate().toISOString() : undefined,
        } as Document;
      });

      setDocuments(data);

      // Notify parent of document changes for compliance checklist refresh
      if (onDocumentsChange) {
        onDocumentsChange(data);
      }
    } catch (error: any) {
      toast({
        title: "Error loading documents",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getDocTypeColor = (type: string) => {
    switch (type) {
      case "floor_plan":
        return "default";
      case "layout":
        return "secondary";
      case "contract":
        return "outline";
      default:
        return "secondary";
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      // document_url already contains the full download URL from Firebase Storage
      const a = document.createElement("a");
      a.href = doc.document_url;
      a.download = doc.document_name;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error: any) {
      toast({
        title: "Error downloading document",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleCategoryChange = async (docId: string, newCategory: string) => {
    try {
      await dualWriteJobDocument(contractorId, jobId, docId, {
        category: newCategory || null,
        complianceCategory: newCategory || null,
        status: newCategory ? 'uploaded' : null,
      });

      toast({
        title: "Category Updated",
        description: newCategory
          ? `Document linked to "${COMPLIANCE_CATEGORIES.find(c => c.value === newCategory)?.label}"`
          : "Document category removed",
      });

      fetchDocuments();
    } catch (error: any) {
      toast({
        title: "Error updating category",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getCategoryLabel = (categoryValue: string | null) => {
    if (!categoryValue) return null;
    return COMPLIANCE_CATEGORIES.find(c => c.value === categoryValue)?.label || categoryValue;
  };

  const handleDeleteDocument = async (docToDelete: Document) => {
    if (!window.confirm(`Are you sure you want to delete "${docToDelete.document_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Delete from Firebase Storage if URL exists
      if (docToDelete.document_url) {
        try {
          // Extract storage path from URL
          const storageUrl = new URL(docToDelete.document_url);
          const pathMatch = storageUrl.pathname.match(/\/o\/(.+?)\?/);
          if (pathMatch) {
            const storagePath = decodeURIComponent(pathMatch[1]);
            const storageRef = ref(storage, storagePath);
            await deleteObject(storageRef);
          }
        } catch (storageError: any) {
          // Log but don't fail if storage delete fails (file might not exist)
          console.warn("Could not delete from storage:", storageError.message);
        }
      }

      // Delete from both Firestore paths (dual-write pattern)
      const batch = writeBatch(db);

      // Delete from new path: contractors/{contractorId}/jobs/{jobId}/documents/{docId}
      const newDocRef = doc(db, "contractors", contractorId, "jobs", jobId, "documents", docToDelete.id);
      batch.delete(newDocRef);

      // Delete from old path: job_documents/{docId}
      const oldDocRef = doc(db, "job_documents", docToDelete.id);
      batch.delete(oldDocRef);

      await batch.commit();

      toast({
        title: "Document Deleted",
        description: `"${docToDelete.document_name}" has been deleted.`,
      });

      fetchDocuments();
    } catch (error: any) {
      toast({
        title: "Error deleting document",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Documents & Floor Plans</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowImportVault(true)}>
                <Archive className="h-4 w-4 mr-2" />
                Import from Vault
              </Button>
              <Button size="sm" onClick={() => setShowUpload(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {documents.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No documents uploaded yet
            </p>
          ) : (
            documents.map((doc) => (
              <Card key={doc.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4 sm:pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-muted-foreground mt-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-sm sm:text-base truncate">{doc.document_name}</h3>
                          {doc.document_type && (
                            <Badge variant={getDocTypeColor(doc.document_type)}>
                              {doc.document_type.replace("_", " ")}
                            </Badge>
                          )}
                          {doc.category && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                              <Tag className="h-3 w-3 mr-1" />
                              {getCategoryLabel(doc.category)}
                            </Badge>
                          )}
                          {doc.extractedFields && doc.extractedFields.length > 0 && (
                            <Badge
                              variant="outline"
                              className="bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300 cursor-pointer"
                              onClick={() => setPreviewDoc(doc)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === 'Enter' && setPreviewDoc(doc)}
                              aria-label="View AI extracted data"
                            >
                              <Sparkles className="h-3 w-3 mr-1" aria-hidden="true" />
                              AI Extracted
                            </Badge>
                          )}
                        </div>
                        {doc.description && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {doc.description}
                          </p>
                        )}
                        {/* Show key extracted field preview */}
                        {doc.extractedFields && doc.extractedFields.length > 0 && (
                          <div className="text-xs text-muted-foreground mb-2 space-y-0.5">
                            {doc.extractedFields.slice(0, 2).map((field, idx) => (
                              field.value && (
                                <div key={idx} className="flex gap-1">
                                  <span className="font-medium">{FIELD_LABELS[field.name] || field.name}:</span>
                                  <span>{String(field.value).substring(0, 30)}{String(field.value).length > 30 ? '...' : ''}</span>
                                </div>
                              )
                            ))}
                            {doc.extractedFields.filter(f => f.value).length > 2 && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-primary hover:underline"
                              >
                                +{doc.extractedFields.filter(f => f.value).length - 2} more fields
                              </button>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Uploaded {doc.createdAt ? format(new Date(doc.createdAt), "MMM d, yyyy") : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 self-end sm:self-start">
                      {doc.extractedFields && doc.extractedFields.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPreviewDoc(doc)}
                          aria-label={`View extracted data for ${doc.document_name}`}
                          className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                        >
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(doc)}
                        aria-label={`Download ${doc.document_name}`}
                        className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                      >
                        <Download className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" aria-label={`More options for ${doc.document_name}`}>
                            <MoreVertical className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel>Change Compliance Category</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {COMPLIANCE_CATEGORIES.map((cat) => (
                            <DropdownMenuItem
                              key={cat.value || "none"}
                              onClick={() => handleCategoryChange(doc.id, cat.value)}
                              className={doc.category === cat.value ? "bg-accent" : ""}
                            >
                              {cat.label}
                              {doc.category === cat.value && " ✓"}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDeleteDocument(doc)}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Document
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {showUpload && (
        <UploadDocument
          jobId={jobId}
          contractorId={contractorId}
          initialCategory={uploadCategory}
          requirementName={uploadRequirement}
          onClose={() => {
            setShowUpload(false);
            setUploadCategory("");
            setUploadRequirement("");
          }}
          onSuccess={() => {
            setShowUpload(false);
            setUploadCategory("");
            setUploadRequirement("");
            fetchDocuments();
          }}
        />
      )}

      <ImportFromVaultModal
        open={showImportVault}
        onOpenChange={setShowImportVault}
        jobId={jobId}
        jobAddress={jobAddress}
        contractorId={contractorId}
        onSuccess={fetchDocuments}
        existingDocumentIds={documents.map(d => d.id)}
      />

      {/* Extraction Preview Modal */}
      {previewDoc && previewDoc.extractedFields && (
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Extracted Data
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Document info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-sm">{previewDoc.document_name}</p>
                {previewDoc.category && (
                  <p className="text-xs text-muted-foreground">
                    Category: {getCategoryLabel(previewDoc.category)}
                  </p>
                )}
                {previewDoc.extractionConfidence && (
                  <p className="text-xs text-muted-foreground">
                    Confidence: {Math.round(previewDoc.extractionConfidence * 100)}%
                    {previewDoc.extractionMethod && ` (${previewDoc.extractionMethod === 'gpt4_vision' ? 'GPT-4 Vision' : 'Pattern Matching'})`}
                  </p>
                )}
              </div>

              {/* Extracted fields */}
              <div className="space-y-2 sm:space-y-3 max-h-[50vh] overflow-y-auto">
                {previewDoc.extractedFields
                  .filter(field => field.value !== null)
                  .map((field, idx) => (
                    <div key={idx} className="flex justify-between items-start p-2 border rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-muted-foreground">
                          {FIELD_LABELS[field.name] || field.name}
                        </p>
                        <p className="text-sm font-medium break-words">
                          {typeof field.value === 'boolean'
                            ? (field.value ? 'Yes' : 'No')
                            : String(field.value)}
                        </p>
                      </div>
                      {field.confidence !== undefined && (
                        <Badge
                          variant="outline"
                          className={
                            field.confidence >= 0.9
                              ? "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
                              : field.confidence >= 0.7
                                ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300"
                                : "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"
                          }
                        >
                          {Math.round(field.confidence * 100)}%
                        </Badge>
                      )}
                    </div>
                  ))}
              </div>

              {/* Empty fields section */}
              {previewDoc.extractedFields.filter(f => f.value === null).length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Fields not found ({previewDoc.extractedFields.filter(f => f.value === null).length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {previewDoc.extractedFields
                      .filter(f => f.value === null)
                      .map((field, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs opacity-50">
                          {FIELD_LABELS[field.name] || field.name}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setPreviewDoc(null)}>
                  Close
                </Button>
                <Button onClick={() => handleDownload(previewDoc)}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Original
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

JobDocuments.displayName = "JobDocuments";
